import { useState } from "react";
import { Bug, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useVulnerabilities,
  useVulnerabilitySummary,
  useUpdateVulnerability,
} from "@/hooks/use-api";
import { cn } from "@/lib/utils";
import type { VulnSeverity, VulnStatus } from "@/types/api";

const PAGE_LIMIT = 25;

const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-red-500/10 text-red-500 border-red-500/30",
  high: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  medium: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  low: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  unknown: "bg-gray-500/10 text-gray-500 border-gray-500/30",
};

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.unknown,
      )}
    >
      {severity}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    open: "bg-red-500/10 text-red-400",
    acknowledged: "bg-yellow-500/10 text-yellow-400",
    resolved: "bg-green-500/10 text-green-400",
    false_positive: "bg-gray-500/10 text-gray-400",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        colors[status] ?? colors.open,
      )}
    >
      {status.replace("_", " ")}
    </span>
  );
}

export default function VulnerabilitiesPage() {
  const [severityFilter, setSeverityFilter] = useState<VulnSeverity | "all">("all");
  const [statusFilter, setStatusFilter] = useState<VulnStatus | "all">("all");
  const [offset, setOffset] = useState(0);

  const { data: summaryData } = useVulnerabilitySummary();
  const summary = summaryData?.summary;

  const { data, isLoading, isError, refetch } = useVulnerabilities({
    severity: severityFilter === "all" ? undefined : severityFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    limit: PAGE_LIMIT,
    offset,
  });

  const updateVuln = useUpdateVulnerability();
  const vulns = data?.vulnerabilities ?? [];
  const hasNext = vulns.length === PAGE_LIMIT;
  const hasPrev = offset > 0;

  function handleStatusUpdate(vulnId: string, status: VulnStatus) {
    updateVuln.mutate({ vulnId, status });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bug className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Vulnerabilities</h1>
            <p className="text-sm text-muted-foreground">
              Software vulnerability management across monitored assets
            </p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {(["critical", "high", "medium", "low", "unknown"] as const).map((sev) => (
            <Card
              key={sev}
              className={cn(
                "cursor-pointer transition-colors hover:bg-muted/50",
                severityFilter === sev && "ring-2 ring-primary",
              )}
              onClick={() => {
                setSeverityFilter(severityFilter === sev ? "all" : sev);
                setOffset(0);
              }}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium capitalize text-muted-foreground">
                  {sev}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{summary[sev]}</p>
              </CardContent>
            </Card>
          ))}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Open</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{summary.total}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={severityFilter}
          onChange={(e) => {
            setSeverityFilter(e.target.value as VulnSeverity | "all");
            setOffset(0);
          }}
          aria-label="Filter by severity"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="unknown">Unknown</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as VulnStatus | "all");
            setOffset(0);
          }}
          aria-label="Filter by status"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="resolved">Resolved</option>
          <option value="false_positive">False Positive</option>
        </select>

        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isLoading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load vulnerabilities. Data will retry automatically.
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left font-medium">CVE</th>
              <th className="px-4 py-3 text-left font-medium">Severity</th>
              <th className="px-4 py-3 text-left font-medium">CVSS</th>
              <th className="px-4 py-3 text-left font-medium">Package</th>
              <th className="px-4 py-3 text-left font-medium">Version</th>
              <th className="px-4 py-3 text-left font-medium">Fix</th>
              <th className="px-4 py-3 text-left font-medium">Host</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : vulns.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  No vulnerabilities found.
                </td>
              </tr>
            ) : (
              vulns.map((v) => (
                <tr key={v.id} className="border-b transition-colors hover:bg-muted/50">
                  <td className="px-4 py-3 font-mono text-xs">
                    <a
                      href={`https://nvd.nist.gov/vuln/detail/${v.cve_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {v.cve_id}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <SeverityBadge severity={v.severity} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {v.cvss_score ?? "-"}
                  </td>
                  <td className="px-4 py-3">{v.package_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.package_version}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.fixed_version ?? "-"}</td>
                  <td className="px-4 py-3">{v.hostname}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={v.status} />
                  </td>
                  <td className="px-4 py-3">
                    {v.status === "open" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusUpdate(v.id, "acknowledged")}
                        disabled={updateVuln.isPending}
                        aria-label={`Acknowledge ${v.cve_id}`}
                      >
                        Ack
                      </Button>
                    )}
                    {v.status === "acknowledged" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStatusUpdate(v.id, "resolved")}
                        disabled={updateVuln.isPending}
                        aria-label={`Resolve ${v.cve_id}`}
                      >
                        Resolve
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && vulns.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1}-{offset + vulns.length}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
              disabled={!hasPrev}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_LIMIT)}
              disabled={!hasNext}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
